import { useCallback, useEffect, useMemo, useState } from 'react';

import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { ADMIN_MODULE_LABELS } from '../../../constants/adminModules';
import {
  createAdminModerateur,
  fetchAdminEquipeModules,
  fetchAdminModerateurs,
  resendModerateurAccess,
  updateModerateurModules,
  updateModerateurStatut,
} from '../../../services/academiaAdminService';
import {
  AcademicPanelCard,
  AcademicPanelStatBanner,
  AcademicPanelTableWrap,
} from './AcademicPanelCard';

type ModerateurRow = {
  id: string;
  nomComplet: string;
  prenom: string;
  nom: string;
  telephone: string;
  email?: string | null;
  estActif: boolean;
  modules: string[];
  dateCreation: string;
};

type ModuleOption = { key: string; label: string };

type ToastState = { message: string; variant: 'success' | 'error' } | null;

const EMPTY_CREATE = {
  prenom: '',
  nom: '',
  telephone: '',
  email: '',
  modules: [] as string[],
};

const BTN_PRIMARY = 'academic-panel-btn academic-panel-btn--primary';
const BTN_SECONDARY = 'academic-panel-btn academic-panel-btn--secondary';
const BTN_DANGER = 'academic-panel-btn academic-panel-btn--danger';

const fieldInputClass =
  'tarif-field__input w-full min-w-0 rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';

type KpiTone = 'primary' | 'emerald' | 'sky';

const KPI_TONE_CLASS: Record<KpiTone, string> = {
  primary: 'border-outline-variant/25 bg-gradient-to-br from-white to-surface-container-low',
  emerald: 'border-emerald-200/50 bg-gradient-to-br from-emerald-50/70 to-white',
  sky: 'border-sky-200/50 bg-gradient-to-br from-sky-50/80 to-white',
};

const KPI_LABEL_CLASS: Record<KpiTone, string> = {
  primary: 'text-on-surface-variant',
  emerald: 'text-emerald-800',
  sky: 'text-sky-800',
};

const KPI_VALUE_CLASS: Record<KpiTone, string> = {
  primary: 'text-primary',
  emerald: 'text-emerald-900',
  sky: 'text-sky-900',
};

function EquipeKpiTile({
  label,
  value,
  hint,
  icon,
  tone = 'primary',
}: {
  label: string;
  value: string | number;
  hint?: string;
  icon: string;
  tone?: KpiTone;
}) {
  return (
    <article className={`rounded-xl border p-4 shadow-sm ${KPI_TONE_CLASS[tone]}`}>
      <div className="flex items-start justify-between gap-3">
        <p className={`text-xs font-bold uppercase tracking-wide ${KPI_LABEL_CLASS[tone]}`}>{label}</p>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/80 text-primary shadow-sm">
          <LucideSymbolIcon name={icon} tone="inherit" size={18} />
        </span>
      </div>
      <p className={`mt-2 text-3xl font-black tabular-nums ${KPI_VALUE_CLASS[tone]}`}>{value}</p>
      {hint ? <p className={`mt-1 text-xs ${KPI_LABEL_CLASS[tone]}`}>{hint}</p> : null}
    </article>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={
        active
          ? 'academic-panel-badge academic-panel-badge--success'
          : 'academic-panel-badge academic-panel-badge--muted'
      }
    >
      {active ? 'Actif' : 'Inactif'}
    </span>
  );
}

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function AcademiaEquipePanel() {
  const [rows, setRows] = useState<ModerateurRow[]>([]);
  const [moduleOptions, setModuleOptions] = useState<ModuleOption[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [editModules, setEditModules] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);

  const selected = useMemo(() => rows.find((r) => r.id === selectedId) ?? null, [rows, selectedId]);

  const stats = useMemo(() => {
    const actifs = rows.filter((r) => r.estActif).length;
    return {
      total: rows.length,
      actifs,
      inactifs: rows.length - actifs,
    };
  }, [rows]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [moderateurs, modules] = await Promise.all([fetchAdminModerateurs(), fetchAdminEquipeModules()]);
      setRows(moderateurs);
      setModuleOptions(modules);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Chargement impossible', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useRegisterPortalRefresh(loadData);

  useEffect(() => {
    if (selected) {
      setEditModules([...selected.modules]);
    } else {
      setEditModules([]);
    }
  }, [selected]);

  const runAction = async (action: () => Promise<string>, fallbackError: string) => {
    try {
      const message = await action();
      showToast(message, 'success');
      await loadData();
    } catch (e) {
      showToast(e instanceof Error ? e.message : fallbackError, 'error');
    }
  };

  return (
    <AdminPageShell
      title="Équipe & accès"
      description="Créez des comptes modérateurs et définissez les modules du portail admin auxquels chaque personne a accès."
      sideCard={{
        label: 'Modérateurs',
        value: loading ? '…' : String(stats.total),
        helper: stats.total > 0 ? `${stats.actifs} actif${stats.actifs > 1 ? 's' : ''} · accès limité par module` : 'Comptes délégués avec accès limité par module',
        sideLinks: [{ label: 'Sécurité & traçabilité', to: '/admin/security', icon: 'shield' }],
      }}
      isLoading={loading && rows.length === 0 && moduleOptions.length === 0}
      onRetry={() => void loadData()}
      loadingMessage="Chargement de l'équipe…"
    >
      <AdminToast message={toast?.message ?? null} variant={toast?.variant} onClose={() => setToast(null)} />

      <div className="space-y-6">
        <section className="grid gap-3 sm:grid-cols-3">
          <EquipeKpiTile label="Modérateurs" value={loading ? '…' : stats.total} hint="Comptes délégués" icon="groups" />
          <EquipeKpiTile
            label="Actifs"
            value={loading ? '…' : stats.actifs}
            hint="Peuvent se connecter au portail"
            icon="verified_user"
            tone="emerald"
          />
          <EquipeKpiTile
            label="Inactifs"
            value={loading ? '…' : stats.inactifs}
            hint="Accès suspendu"
            icon="person_off"
            tone="sky"
          />
        </section>

        <AcademicPanelStatBanner tone="info">
          L&apos;administrateur conserve tous les droits. Les modérateurs n&apos;accèdent qu&apos;aux modules cochés
          ci-dessous, sur le même portail admin.
        </AcademicPanelStatBanner>

        {showCreate ? (
          <AcademicPanelCard
            variant="accent"
            icon="person_add"
            title="Inviter un modérateur"
            subtitle="Mot de passe provisoire à 6 chiffres envoyé par e-mail · menu admin limité aux modules cochés"
            footer={
              <button
                type="button"
                disabled={creating}
                className={`${BTN_PRIMARY} disabled:opacity-60`}
                onClick={() => {
                  if (!createForm.email.trim()) {
                    showToast('E-mail requis', 'error');
                    return;
                  }
                  if (createForm.modules.length === 0) {
                    showToast('Sélectionnez au moins un module', 'error');
                    return;
                  }
                  void (async () => {
                    setCreating(true);
                    try {
                      const r = await createAdminModerateur(createForm);
                      setShowCreate(false);
                      setCreateForm(EMPTY_CREATE);
                      await loadData();
                      showToast(r?.message ?? 'Modérateur créé', 'success');
                    } catch (e) {
                      showToast(e instanceof Error ? e.message : 'Échec de la création', 'error');
                    } finally {
                      setCreating(false);
                    }
                  })();
                }}
              >
                <LucideSymbolIcon name="mail" tone="inherit" size={18} />
                {creating ? 'Envoi…' : 'Inviter par e-mail'}
              </button>
            }
          >
            <div className="tarif-field-grid">
              {(
                [
                  ['prenom', 'Prénom', 'text'],
                  ['nom', 'Nom', 'text'],
                  ['telephone', 'Téléphone', 'tel'],
                  ['email', 'E-mail *', 'email'],
                ] as const
              ).map(([key, label, type]) => (
                <label key={key} className="tarif-field">
                  <span className="tarif-field__label">{label}</span>
                  <input
                    type={type}
                    autoComplete="off"
                    className={fieldInputClass}
                    placeholder={key === 'telephone' ? 'Optionnel' : undefined}
                    value={createForm[key]}
                    onChange={(e) => setCreateForm((f) => ({ ...f, [key]: e.target.value }))}
                  />
                </label>
              ))}
            </div>
            <div className="mt-5">
              <p className="mb-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                Modules autorisés
              </p>
              <ModuleCheckboxGrid
                options={moduleOptions}
                selected={createForm.modules}
                onChange={(modules) => setCreateForm((f) => ({ ...f, modules }))}
              />
            </div>
          </AcademicPanelCard>
        ) : null}

        <div className="grid gap-6 xl:grid-cols-[1.15fr_1fr]">
          <AcademicPanelCard
            icon="manage_accounts"
            title="Annuaire modérateurs"
            subtitle={loading ? 'Chargement…' : `${rows.length} compte${rows.length > 1 ? 's' : ''} enregistré${rows.length > 1 ? 's' : ''}`}
            badge={
              <div className="flex flex-wrap items-center justify-end gap-2">
                {rows.length > 0 ? (
                  <span className="academic-panel-badge academic-panel-badge--success">
                    {stats.actifs} actif{stats.actifs > 1 ? 's' : ''}
                  </span>
                ) : null}
                <button
                  type="button"
                  className={showCreate ? BTN_SECONDARY : BTN_PRIMARY}
                  onClick={() => {
                    setShowCreate((v) => {
                      if (!v) {
                        setToast(null);
                        setCreateForm(EMPTY_CREATE);
                      }
                      return !v;
                    });
                  }}
                >
                  <LucideSymbolIcon name={showCreate ? 'close' : 'person_add'} tone="inherit" size={16} />
                  {showCreate ? 'Fermer' : 'Nouveau modérateur'}
                </button>
              </div>
            }
            flush
          >
            <AcademicPanelTableWrap
              empty={
                rows.length === 0
                  ? loading
                    ? <p className="text-sm text-on-surface-variant">Chargement des modérateurs…</p>
                    : (
                        <p className="text-sm text-on-surface-variant">
                          Aucun modérateur pour le moment. Invitez un collaborateur pour déléguer des tâches.
                        </p>
                      )
                  : undefined
              }
            >
              {rows.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="academic-panel-table min-w-full text-left text-sm">
                    <thead>
                      <tr>
                        <th>Modérateur</th>
                        <th>Modules</th>
                        <th>Créé le</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const isSelected = selectedId === row.id;
                        return (
                          <tr
                            key={row.id}
                            className={[
                              'cursor-pointer transition-colors',
                              isSelected ? 'bg-primary/[0.06]' : 'hover:bg-surface-container-low/80',
                            ].join(' ')}
                            onClick={() => setSelectedId(row.id)}
                          >
                            <td>
                              <div className="flex items-center gap-3">
                                <span
                                  className={[
                                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-black',
                                    isSelected
                                      ? 'bg-primary text-white'
                                      : 'bg-surface-container-high text-primary',
                                  ].join(' ')}
                                >
                                  {(row.prenom?.[0] ?? row.nomComplet?.[0] ?? 'M').toUpperCase()}
                                </span>
                                <div className="min-w-0">
                                  <p className="font-semibold text-slate-900">{row.nomComplet}</p>
                                  <p className="truncate text-xs text-on-surface-variant">
                                    {row.email ?? row.telephone}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="inline-flex items-center gap-1 rounded-full bg-surface-container-high px-2.5 py-1 text-xs font-bold text-slate-700">
                                <LucideSymbolIcon name="widgets" tone="inherit" size={14} />
                                {row.modules.length}
                              </span>
                            </td>
                            <td className="text-on-surface-variant">{formatDate(row.dateCreation)}</td>
                            <td>
                              <StatusBadge active={row.estActif} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </AcademicPanelTableWrap>
          </AcademicPanelCard>

          <AcademicPanelCard
            variant={selected ? 'preview' : 'default'}
            icon="admin_panel_settings"
            title="Accès délégués"
            subtitle={
              selected
                ? `Configuration pour ${selected.nomComplet}`
                : 'Sélectionnez un modérateur dans l’annuaire'
            }
            badge={selected ? <StatusBadge active={selected.estActif} /> : null}
            footer={
              selected ? (
                <>
                  <button
                    type="button"
                    className={BTN_PRIMARY}
                    onClick={() => {
                      if (editModules.length === 0) {
                        showToast('Au moins un module requis', 'error');
                        return;
                      }
                      void runAction(async () => {
                        await updateModerateurModules(selected.id, editModules);
                        return 'Accès enregistrés';
                      }, 'Mise à jour impossible');
                    }}
                  >
                    <LucideSymbolIcon name="save" tone="inherit" size={16} />
                    Enregistrer
                  </button>
                  <button
                    type="button"
                    className={BTN_SECONDARY}
                    onClick={() =>
                      void runAction(async () => {
                        const r = await resendModerateurAccess(selected.id);
                        return r?.message ?? 'E-mail envoyé';
                      }, 'Renvoi impossible')
                    }
                  >
                    <LucideSymbolIcon name="forward_to_inbox" tone="inherit" size={16} />
                    Renvoyer l&apos;accès
                  </button>
                  <button
                    type="button"
                    className={selected.estActif ? BTN_DANGER : BTN_PRIMARY}
                    onClick={() =>
                      void runAction(async () => {
                        await updateModerateurStatut(selected.id, !selected.estActif);
                        return selected.estActif ? 'Compte désactivé' : 'Compte activé';
                      }, 'Changement de statut impossible')
                    }
                  >
                    <LucideSymbolIcon
                      name={selected.estActif ? 'block' : 'check_circle'}
                      tone="inherit"
                      size={16}
                    />
                    {selected.estActif ? 'Désactiver' : 'Activer'}
                  </button>
                </>
              ) : undefined
            }
          >
            {!selected ? (
              <div className="academic-panel-empty flex flex-col items-center gap-3 py-10 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-container-high text-primary">
                  <LucideSymbolIcon name="touch_app" tone="inherit" size={28} />
                </span>
                <p>Sélectionnez un modérateur pour ajuster ses modules et son statut.</p>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="rounded-xl border border-outline-variant/15 bg-surface-container-low/60 p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-on-surface-variant">Identité</p>
                  <p className="mt-1 text-lg font-bold text-slate-900">{selected.nomComplet}</p>
                  <div className="mt-2 flex flex-wrap gap-3 text-sm text-on-surface-variant">
                    {selected.email ? (
                      <span className="inline-flex items-center gap-1.5">
                        <LucideSymbolIcon name="mail" tone="inherit" size={16} />
                        {selected.email}
                      </span>
                    ) : null}
                    <span className="inline-flex items-center gap-1.5">
                      <LucideSymbolIcon name="phone" tone="inherit" size={16} />
                      {selected.telephone}
                    </span>
                  </div>
                </div>

                <div>
                  <p className="mb-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                    Modules autorisés
                  </p>
                  <ModuleCheckboxGrid
                    options={moduleOptions}
                    selected={editModules}
                    onChange={setEditModules}
                  />
                </div>

                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                    Accès actuellement enregistrés
                  </p>
                  {selected.modules.length === 0 ? (
                    <p className="text-sm text-on-surface-variant">Aucun module assigné.</p>
                  ) : (
                    <ul className="flex flex-wrap gap-2">
                      {selected.modules.map((key) => (
                        <li
                          key={key}
                          className="inline-flex items-center gap-1.5 rounded-full border border-primary/15 bg-primary/5 px-3 py-1 text-xs font-bold text-primary"
                        >
                          <LucideSymbolIcon name="check" tone="inherit" size={14} />
                          {ADMIN_MODULE_LABELS[key as keyof typeof ADMIN_MODULE_LABELS] ?? key}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </AcademicPanelCard>
        </div>
      </div>
    </AdminPageShell>
  );
}

function ModuleCheckboxGrid({
  options,
  selected,
  onChange,
}: {
  options: ModuleOption[];
  selected: string[];
  onChange: (modules: string[]) => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((opt) => {
        const checked = selected.includes(opt.key);
        return (
          <label
            key={opt.key}
            className={[
              'group flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-sm transition',
              checked
                ? 'border-primary/40 bg-primary/[0.06] ring-1 ring-primary/15'
                : 'border-outline-variant/20 bg-white hover:border-primary/25 hover:bg-surface-container-low/80',
            ].join(' ')}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-outline-variant/40 text-primary focus:ring-primary/30"
              checked={checked}
              onChange={(e) => {
                const next = e.target.checked
                  ? Array.from(new Set([...selected, opt.key]))
                  : selected.filter((k) => k !== opt.key);
                onChange(next);
              }}
            />
            <span className="min-w-0 flex-1">
              <span className="block font-semibold text-slate-900">{opt.label}</span>
              <span className="mt-0.5 block text-xs text-on-surface-variant">{opt.key}</span>
            </span>
            <LucideSymbolIcon
              name={checked ? 'check_circle' : 'radio_button_unchecked'}
              tone={checked ? 'primary' : 'inherit'}
              size={18}
              className={checked ? 'opacity-100' : 'opacity-30 group-hover:opacity-60'}
            />
          </label>
        );
      })}
    </div>
  );
}
